import React from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  DRILLDOWN_SHEET_BODY_SX,
  DRILLDOWN_SHEET_BORDER_RADIUS,
  DRILLDOWN_SHEET_HEADER_LAYER_SOFT_SX,
  DRILLDOWN_SHEET_HEADER_LAYER_STRONG_SX,
  DRILLDOWN_SHEET_HEADER_SX,
  DRILLDOWN_SHEET_HEADER_SUBTITLE_SX,
  DRILLDOWN_SHEET_MOBILE_HEIGHT,
  DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
  DRILLDOWN_SHEET_BODY_SCROLL_SX,
} from './analyticsDrilldownStyles';

/** Canonical desktop width for analytics drill-down sheets. */
export const ANALYTICS_SHEET_PAPER_MAX_WIDTH = 520;

/**
 * Shared modal chrome for the analytics page.
 *
 * Mobile (sm-down): renders pinned to the bottom edge of the screen at
 * full viewport width, with only the top two corners rounded. There is
 * no grab handle — the close button on the header is the canonical
 * dismiss affordance.
 *
 * Desktop (sm+): renders as a centered card sized to fit content up to 85vh.
 */
const BottomSheet = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 'sm',
  paperMaxWidth = ANALYTICS_SHEET_PAPER_MAX_WIDTH,
}) => {
  const theme = useTheme();
  const isMobileSheet = useMediaQuery(theme.breakpoints.down('sm'));

  const headerBlock = (title || subtitle) && (
    <Box sx={DRILLDOWN_SHEET_HEADER_SX}>
      <Box aria-hidden="true" sx={DRILLDOWN_SHEET_HEADER_LAYER_STRONG_SX} />
      <Box aria-hidden="true" sx={DRILLDOWN_SHEET_HEADER_LAYER_SOFT_SX} />
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {title && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '1.05rem',
                color: 'rgba(0,0,0,0.9)',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle &&
            (typeof subtitle === 'string' ? (
              <Typography variant="caption" sx={DRILLDOWN_SHEET_HEADER_SUBTITLE_SX}>
                {subtitle}
              </Typography>
            ) : (
              subtitle
            ))}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ mt: 0, flexShrink: 0 }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      hideBackdrop={isMobileSheet}
      slotProps={{
        backdrop: isMobileSheet
          ? undefined
          : {
              sx: { bgcolor: 'rgba(0,0,0,0.32)' },
            },
      }}
      PaperProps={{
        sx: {
          borderRadius: DRILLDOWN_SHEET_BORDER_RADIUS,
          ...DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
          m: { xs: 0, sm: 2 },
          mt: { xs: 'auto', sm: 2 },
          width: { xs: '100%', sm: paperMaxWidth },
          maxWidth: {
            xs: '100%',
            sm: paperMaxWidth,
          },
          height: { xs: DRILLDOWN_SHEET_MOBILE_HEIGHT, sm: 'auto' },
          maxHeight: { xs: DRILLDOWN_SHEET_MOBILE_HEIGHT, sm: '85vh' },
          bgcolor: '#fff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {headerBlock}
      <Box sx={DRILLDOWN_SHEET_BODY_SCROLL_SX}>
        <Box sx={DRILLDOWN_SHEET_BODY_SX}>{children}</Box>
      </Box>
    </Dialog>
  );
};

export default BottomSheet;

// Re-export layout tokens for sibling drill-down dialogs.
export {
  DRILLDOWN_SHEET_BORDER_RADIUS,
  DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
};
